import AdminInterviewCard from '@components/AdminInterviewCard/AdminInterviewCard.component'
import AdminPageHeader from '@components/AdminPageHeader/AdminPageHeader.component'
import { Box, Button, MenuItem, styled, TextField, Typography, CircularProgress, FormLabel, TextareaAutosize, InputAdornment, IconButton } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import axios from 'axios'
import AndroidIcon from 'icons/Technologies/Android.icon'
import { useState, useEffect } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Route as AdminDashboardRoute } from './_admin.admin-dashboard';
import NoData from 'icons/NoData.icon'
import AdminCategoryCard from '@components/AdminCategoryCard/AdminCategoryCard.component'
import { toast } from 'react-toastify'
import InterviewCategoryCard from '@components/InterviewCategoryCard/InterviewCategoryCard'
import ReusableModal from '@components/Modal/ReusableModal.component'
import SearchIcon from 'icons/Search.icon'
import AddIcon from '@mui/icons-material/Add';

export const Route = createFileRoute('/(admin)/_admin/create-interview')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      interviewId: search.interviewId as string | undefined,
    }
  },
})
interface CreateInterviewDto {
  technologyId: string;
  difficulty: string;
  readTime: string;
  answerTime: string;
  description: string;
  name: string;
}

const StyledTextArea = styled(TextareaAutosize)({
    backgroundColor: '#F9F9F9',
    width: '100%',
    minHeight: '135px',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid var(--Interviewer-Lightest-Black)',
    fontFamily: 'inherit',
    fontSize: '14px',
    lineHeight: '20px',
    resize: 'vertical',
    '&:focus': {
    outline: 'none',
    },
})

const StyledInterviewContent = styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: '#fff',
    padding: '20px',
    border: '1px solid var(--Light-Gray)',
    borderRadius: '12px',
    boxShadow: '0px 1px 2px 0px #0205111A'
})

const StyledSearch = styled(TextField)(() => ({
  marginTop: '40px',
  input: {
    lineHeight: '20px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--Interviewer-Lightest-Black)',
  },
  '@media (max-width: 768px)': {
    marginTop: '16px',
  }
}));

function RouteComponent() {
  const navigate = useNavigate()
  const { interviewId } = Route.useSearch()
  const queryClient = useQueryClient();

  
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedTechnology, setSelectedTechnology] = useState('')
  const [selectedDifficulty, setSelectedDifficulty] = useState('')


  const [isCreatedInterview, setIsCreatedInterview] = useState(false)
  const [idOfCreatedInterview, setIdOfCreatedInterview] = useState<string | null>(null);

  const [errors, setErrors] = useState({
    name: "",
    // description: "",
    difficulty: "",
    technology: ""
  });


  const [isModalOpen, setModalOpen] = useState(false);

  // Postojece tehnologije
  const { data: technologies, isLoading: isLoadingTechnologies, error: technologiesError } = useQuery({
    queryKey: ['technologies', 'create-interview'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://interviewer.runasp.net/api/technologies', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
  });


  //Ako je edit stranica setuj mi id intervjua
  useEffect(() => {
    if (interviewId) {
      setIdOfCreatedInterview(interviewId)
    }
  }, [interviewId])


  // Prikupljanje podataka intervjua za edit
  const { data: existingInterview, isLoading: isLoadingExistingInterview } = useQuery({
    queryKey: ['existingInterview', interviewId],
    queryFn: async () => {
      if (!interviewId) return null;
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://interviewer.runasp.net/api/interviews/${interviewId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log('editStranica', response);
      
      return response.data;
    },
    enabled: !!interviewId,
  });


  useEffect(() => {
    if (!existingInterview) return;

    setName(existingInterview.name);
    setDescription(existingInterview.description);
    // setSelectedTechnology(existingInterview.technologyId);
    setSelectedDifficulty(existingInterview.difficulty);

  }, [existingInterview])


  // Update interview mutation
  const updateInterviewMutation = useMutation({
    mutationFn: async (interviewData: CreateInterviewDto) => {
      const token = localStorage.getItem('token');
      const response = await axios.put(`https://interviewer.runasp.net/api/interviews/${interviewId}`, interviewData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Interview updated successfully')
    },
    onError: (error) => {
      console.error('Error updating interview:', error);
    },
  });

  // Prikupljanje postojecih globalnih kategorija
  const { data: categories, isLoading: isLoadingCategories, error: categoriesError } = useQuery({
    queryKey: ['categories', 'create-interview'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://interviewer.runasp.net/api/categories', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log('kategorije', response);
      return response.data;
    },
  });

  //Validacija polja
  const validate = () => {
    let valid = true
    const newErrors = { name: "", difficulty: "", technology: ""};

    if(name.trim().length < 1){
      newErrors.name = 'Please add name';
      valid = false;
    }

    // if(description.trim().length < 1){
    //   newErrors.description = 'Please add description';
    //   valid = false;
    // }

    if(selectedDifficulty.trim().length < 1){
      newErrors.difficulty = 'Please add difficulty';
      valid = false;
    }
    
    if(selectedTechnology.trim().length < 1){
      newErrors.technology = 'Please add technology';
      valid = false;
    }

    setErrors(newErrors);
    return valid
  }

  // Kreiranje intervjua
  const handleCreateInterview = () => {
    if (!validate()) return;

    const interviewData = {
        technologyId: selectedTechnology,
        difficulty: selectedDifficulty,
        readTime: "10",
        answerTime: "20",
        description: description,
        name: name,
    };

    if (interviewId) {
      updateInterviewMutation.mutate(interviewData);
    } else {
      createInterviewMutation.mutate(interviewData);
    }
  };

  const createInterviewMutation = useMutation({
    mutationFn: async (interviewData: CreateInterviewDto) => {
      const token = localStorage.getItem('token');
      const response = await axios.post('https://interviewer.runasp.net/api/interviews', interviewData, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      return response;
    },
    onSuccess: (data) => {
      setIdOfCreatedInterview(data.data);
      setIsCreatedInterview(true);
      toast.success('Successfully created interview!')
    },
    onError: (error) => {
      console.log(error);
    }
  });
  // Kraj kreiranja intervjua

  //Pribavljanje kontenta
  const {data: interviewContent} = useQuery({
    queryKey: ['newtest'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://interviewer.runasp.net/api/interviews/${idOfCreatedInterview}/content`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      })
      console.log('pribavljanje Kontenta', response);
      return response.data;
    },
    enabled: !!idOfCreatedInterview
  }) 

  // Povezivanje kategorije na kreirani intervju
  const addCategoryToInterviewMutation = useMutation({
    mutationKey: ['admin-link-category'],
    mutationFn: async (categoryId) => {
      const token = localStorage.getItem('token');
      const response = await axios.put(`https://interviewer.runasp.net/api/interviews/${idOfCreatedInterview}/link-category`,
        {categoryId: categoryId}, 
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        })
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['newtest'] });
      queryClient.invalidateQueries({ queryKey: ['testaa'] });
      console.log(data);
    },
    onError: (error) => {
      console.log(error);
    }
  })


  // Linkovane kategorije za intervju
  const {data: linkedCategories} = useQuery({
    queryKey: ['testaa'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://interviewer.runasp.net/api/interviews/${idOfCreatedInterview}/categories`, 
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )
      console.log('linkovane', response.data);
      return response.data;
    },
    enabled: !!idOfCreatedInterview
  })

  return (
    <Box>
       <AdminPageHeader
        title={interviewId ? "Edit Interview" : "New Interview"}
        icon={<ArrowBackIcon color="primary" fontSize="medium" />} 
        onIconClick={() => navigate({ to: AdminDashboardRoute.to })}
      />
      <Box sx={{
        px: {
          xs: '16px',
          md: '32px'    
        },
        py: {
          xs: '16px',
          md: '32px'
        }
      }}>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: {xs: '20px', md: '48px'}, mb: '64px'}}>
          <TextField 
            label="Interview Name"
            variant="outlined"
            fullWidth
            value={name}
            onChange={e => setName(e.target.value)}
            error={!!errors.name}
            // helperText={errors.name}
            // disabled={interviewCreated && !interviewId}
          />
          <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, gap: '20px'}}>
            <TextField 
              label="Select a technology"
              variant="outlined"
              fullWidth
              select
              value={selectedTechnology}
              onChange={(e) => setSelectedTechnology(e.target.value)}
              error={!!errors.technology}
            >
              {technologies?.map((technology: any) => (
                <MenuItem key={technology.id} value={technology.id}>
                  {technology.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField 
              label="Select a difficulty"
              variant="outlined"
              fullWidth
              select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              error={!!errors.difficulty}
            >
              <MenuItem value="entry">Entry</MenuItem>
              <MenuItem value="advanced">Advanced</MenuItem>
              <MenuItem value="professional">Professional</MenuItem>
            </TextField>
          </Box>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
            <StyledTextArea
              placeholder='Short Interview Description ...'
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end'}}>
            <Button 
              variant='contained' 
              sx={{minWidth: '180px'}}
              onClick={handleCreateInterview}
            >
              {interviewId ? 'Update Interview' : 'Publish Interview'}
            </Button>
          </Box>
        </Box>
        
        {(isCreatedInterview || interviewId) && (
          <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, gap: '20px'}}>
              <Box sx={{width: '100%'}}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: '16px'
                  }}>
                    <Typography variant='h2' sx={{}}>Add a category</Typography>
                    <Button variant='contained' onClick={() => setModalOpen(true)}>Link existing category</Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px'}}>
                    {linkedCategories && linkedCategories.length > 0 ? (
                      linkedCategories.map((category) => (
                        <AdminCategoryCard 
                          key={category.id}
                          id={category.id}
                          name={category.name}
                          description={category.description}
                        />

                      ))
                    ) : (
                      <Typography>No linked categories yet.</Typography>
                    )}
                  </Box>
              </Box>
              <Box sx={{width: '100%'}}>
                  <Typography variant='h2' sx={{pb: '16px'}}>Interview Content</Typography>
                  <StyledInterviewContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%" }}>
                      {interviewContent?.categories?.map((category) => (
                        <Box 
                          key={category.id} 
                          sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
                        >
                          <Typography variant="h2" sx={{fontSize: '16px'}}>{category.name}</Typography>

                          {/* Questions list */}
                          <Box sx={{ pl: 3, display: "flex", flexDirection: "column", gap: "12px" }}>
                            {category.questions.map((question, index) => (
                              <Typography key={index} sx={{ fontSize: "14px", color: 'var(--Interviewer-Black)' }}>
                                <strong>Question {index + 1}:</strong> {question.text}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </StyledInterviewContent>
              </Box>
          </Box>
        )}
      </Box>


         {/* Modal */}
          <ReusableModal open={isModalOpen} onClose={() => setModalOpen(false)}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                textAlign: 'center',
              }}
            >
              <Typography variant="h2" sx={{fontSize: '24px'}}>
                Link existing category
              </Typography>
              <Typography sx={{fontSize: '14px'}}>
                Link a category from the list or search for a specific category
              </Typography>
            </Box>
            <StyledSearch
              fullWidth
              type="search"
              // value={searchQuery}
              // onChange={e => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
              placeholder="Search for a category"
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                mt: '20px',
                width: '100%',
                maxHeight: '400px',
                overflowY: 'auto',
              }}
            >
              {categories?.map(category => {
                  // const isLinked = linkedCategoryIds.includes(category.id);
                  return (
                    <Box key={category.id} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: { xs: 1.5, sm: 2 },
                      border: '1px solid var(--Light-Gray)',
                      borderRadius: '8px',
                      gap: {xs: '4px', md: '0px'},
                      // opacity: isLinked ? 0.5 : 1,
                    }}>
                      <Box>
                        <Typography variant="h3" sx={{pb: '8px'}}>
                          {category.name}
                        </Typography>
                        <Typography sx={{fontSize: '14px'}}>
                          {category.description}
                        </Typography>
                      </Box>
                      <IconButton
                        color="primary"
                        onClick={() => addCategoryToInterviewMutation.mutate(category.id)}
                        // disabled={isLinked}
                        sx={{
                          backgroundColor: 'black',
                          color: 'white',
                          borderRadius: '50%',
                          p: '8px',
                          width: '40px',
                          height: '40px',
                          '&:hover': {
                            backgroundColor: 'var(--Light-Gray)',
                          },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  );
                })
                }
            </Box>
          </ReusableModal>
    </Box>
  )
}
